// English is the source catalogue; zh.ts and it.ts must match its shape.
const en = {
  meta: {
    siteTagline: "Future Electronics for Circularity with Bio-based Materials",
    homeDescription:
      "A curated library of bio-based and biodegradable materials for electronics, each linked to its source publication, alongside projects that use them.",
    materialsTitle: "Materials Database",
    materialsDescription:
      "Browse bio-based and biodegradable materials for electronics. Filter by family, degradation data, tensile strength and source. Each entry links to its source publication.",
    applicationsTitle: "Applications",
    applicationsDescription: "Projects and prototypes that use bio-based and biodegradable materials in electronics, each linked to its source.",
    aboutTitle: "About",
    aboutDescription: "What BioNexus is, how entries are curated and sourced, and how to contribute.",
    materialDescription: (name: string, family?: string | null) =>
      `${name}${family ? ` (${family})` : ""}: measured properties and source publication on BioNexus.`,
    applicationDescription: (title: string) => `${title}: project details, materials used and source on BioNexus.`,
  },
  nav: {
    materials: "Materials",
    applications: "Applications",
    about: "About",
    admin: "Admin",
    light: "Light",
    dark: "Dark",
    auto: "Auto",
    signOut: "Sign out",
    language: "Language",
  },
  common: {
    backHome: "Back to Home",
    backMaterials: "Back to Materials",
    backApplications: "Back to Applications",
    viewAll: "View all",
    readCase: "Read case",
    lastUpdated: (date: string) => `Last updated ${date}`,
    image: "Image",
    source: "Source",
    method: "Method",
    general: "General",
    onThisPage: "On this page",
    notFound: "Page not found",
    returnHome: "Return to Home",
    contentLanguageNote: "",
  },
  applicationTypes: {
    academic: "Academic",
    commercial: "Commercial",
    "open-source": "Open source",
    community: "Community",
  } as Record<string, string>,
  home: {
    heroA: "Future electronics for",
    heroB: "circularity,",
    heroC: "with bio-based materials",
    heroLead: "A curated library of bio-based materials for electronics.",
    exploreMaterials: "Explore materials",
    seeApplications: "See applications",
    statMaterials: "Materials",
    statApplications: "Applications",
    statSources: "Cited sources",
    latestApplications: "Latest Applications",
    featuredMaterials: "Featured Materials",
    noApplications: "No applications available yet.",
    noMaterials: "No materials available yet.",
  },
  loop: {
    stages: ["Grow", "Make", "Use", "Return"],
    aria: "Material life cycle: grow, make, use, return",
    footnote: "End-of-life routes differ per material — see each source.",
  },
  materials: {
    eyebrow: "Materials database",
    title: "Bio-based materials for electronics",
    lead: "Each entry links to its source. Values appear only where the source reports them.",
    allFamilies: "All families",
    filterLabel: "Filter materials by data completeness",
    filters: {
      all: "All data",
      complete: "≥3 measured properties",
      sourced: "With paper source",
      degradation: "Has degradation data",
      tensile: "Has tensile strength",
    },
    count: (n: number) => `${n} ${n === 1 ? "material" : "materials"}`,
    empty: "No materials match these filters.",
    clearFilters: "Clear filters",
    measured: (n: number) => `${n} measured ${n === 1 ? "property" : "properties"}`,
    measuredTitle: "Three or more measured properties, each with a cited source",
  },
  applications: {
    featured: "Featured",
    readTheCase: "Read the case",
    all: "All Applications",
    count: (n: number) => `${n} ${n === 1 ? "case" : "cases"}`,
    emptyTitle: "No Applications Yet",
    emptyText: "Check back soon.",
  },
  material: {
    notFound: "Material Not Found",
    sections: {
      overview: "Overview",
      properties: "Measured Properties",
      abstract: "Abstract",
      fabrication: "How It's Made",
      context: "Research Context",
      applications: "Used In",
    },
    facts: { family: "Family", primaryUse: "Primary use", degradation: "Biodegradation", trl: "TRL · BioNexus assessment" },
    props: {
      degradation: "Biodegradation",
      tensile: "Tensile Strength",
      electrical: "Electrical Performance",
      water: "Water Resistance",
      cost: "Cost",
      trl: "Technology Readiness",
    },
    readFullAbstract: "Read full abstract",
    showLess: "Show less",
    comparedWith: "Compared with",
    referencedFigures: "Referenced figures",
    researchSource: "Research Source",
    viewPaper: "View paper",
    applicationsFromPaper: "Applications (from paper)",
    moreFromFamily: (family: string) => `More from the ${family} family`,
    application: "Application",
  },
  application: {
    notFound: "Application Not Found",
    noDescription: "No description available.",
    sections: {
      overview: "Overview",
      features: "Key Features",
      technology: "How It Works",
      impact: "Sustainability Impact",
      team: "Who's Behind It",
      materials: "Materials Used",
    },
    facts: { type: "Type", category: "Category", year: "Year", trl: "TRL · BioNexus assessment" },
    technologies: "Technologies",
    leadOrganization: "Lead organization",
    projectSource: "Project source",
    tags: "Tags",
    more: "More applications",
  },
  trl: {
    stages: [
      "Basic principles observed",
      "Technology concept formulated",
      "Experimental proof of concept",
      "Technology validated in lab",
      "Technology validated in relevant environment (industrially relevant environment in the case of key enabling technologies)",
      "Technology demonstrated in relevant environment (industrially relevant environment in the case of key enabling technologies)",
      "System prototype demonstration in operational environment",
      "System complete and qualified",
      "Actual system proven in operational environment (competitive manufacturing in the case of key enabling technologies; or in space)",
    ],
    assessedBy: "Assessed by BioNexus against the",
    scale: "EU TRL scale",
    translationNote: "",
    dotsTitle: "TRL assessed by BioNexus",
    aria: (level: number, stage: string) => `Technology readiness level ${level} of 9: ${stage} (assessed by BioNexus)`,
  },
  footer: {
    tagline: "Bio-based materials for electronics",
    navigation: "Navigation",
    contact: "Contact",
    legal: "Legal",
    rights: "All rights reserved.",
    research: "Content for research purposes.",
    colophon: "Colophon & references",
    data: "Data: measured values are taken from the source cited on each entry; values a source does not report are left out.",
    trlBefore: "Technology readiness (TRL) is assessed by BioNexus against the",
    trlLink: "EU Horizon 2020 TRL scale (General Annexes, Part G)",
    images:
      "Images come from the original publications and project pages and are credited and linked on each entry. No AI-generated imagery is used.",
    typefaces: "Typefaces:",
    typefacesAfter: "both under the SIL Open Font License, served by Google Fonts.",
    icons: "Icons:",
    iconsAfter: "(ISC License). Built with React, Tailwind CSS, Radix UI and shadcn/ui.",
    contrastBefore: "Text and interface colours are chosen to meet",
    contrastLink: "WCAG 2.1 AA contrast",
    contrastAfter: "; motion is reduced when the system asks for it.",
    language:
      "Interface available in English, Chinese and Italian. Entry content (descriptions, abstracts, measured values) is shown in the language of its source, usually English.",
  },
  about: {
    title: "About BioNexus",
    whatTitle: "What is BioNexus?",
    whatText:
      "Independent media platform documenting biodegradable materials and their IoT applications. We track peer-reviewed research, real-world projects, and market developments in regenerative electronics.",
    whyTitle: "Why It Matters",
    why1a: "53.6 million metric tons of e-waste generated in 2019",
    why1b: ". As billions of IoT sensors and disposable electronics enter ecosystems, degradability becomes critical.",
    why2a: "Biodegradable electronics polymers market: $126M (2024) → $246M (2033)",
    why2b: ". From medical implants to smart agriculture, materials that perform then dissolve are shifting from research to reality.",
    source1: "UN Global E-waste Monitor 2020",
    source1After: ", ITU/UNITAR/ISWA",
    source2: "Grand View Research",
    source2After: ", Biodegradable Electronics Polymers Market, Oct 2024",
    approachTitle: "Our Approach",
    approachText: "Focus on reliable sources and manual curation for biodegradable electronics research.",
    approachList: [
      "Prioritize peer-reviewed publications",
      "Manual review and verification",
      "Cite sources for all data points",
      "Focus on biodegradable electronics applications",
    ],
    approachNote: "Not a comprehensive database—a curated selection of relevant materials and projects.",
    resourcesTitle: "Related Resources",
    databases: "Material Databases",
    fmb: "Shared platform for materials & works",
    materiom: "Open-source biomaterial recipes",
    contributeTitle: "Contribute",
    contributeText: "Found a relevant paper? Building a biodegradable device?",
    submit: "Submit Publication",
  },
};

export default en;
export type Messages = typeof en;
